import { NS_QUESTIONNAIRE, NS_QUOTATION } from '@/i18n/namespaces';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostFacilitiesQuery } from '../apis';
import { MAYBANK_BANK_CODE, MYS_MOBILE_PREFIX } from '../constants';
import { Answer, Gender, Locale, ReferenceKey, Relationship } from '../enums';
import { useRoute } from './useRoute';
import { DaughterIcon, FemaleIcon, MaleIcon, SonIcon } from '@/assets/icons';
import { IconType } from 'react-icons';
import { OtherData } from '../enums/otherData';

export const useOptions = () => {
  const { t, i18n } = useTranslation(NS_QUESTIONNAIRE);
  const { t: tQuotation } = useTranslation(NS_QUOTATION, {
    keyPrefix: 'forms.quotationForm',
  });

  const { data, isLoading, isError } = usePostFacilitiesQuery();
  const { redirect } = useRoute();

  useEffect(() => {
    if (isError) redirect('error');
  }, [isError]);

  const optionsMap = useMemo(() => {
    const maps = new Map<
      ReferenceKey,
      { industry?: string; label: string; value: string; otherData?: string }[]
    >();

    data?.data.forEach(
      ({
        referenceKey,
        code,
        description,
        descriptionMV,
        industryName,
        otherData,
      }) => {
        const list = maps.get(referenceKey as ReferenceKey) ?? [];
        maps.set(referenceKey as ReferenceKey, [
          ...list,
          {
            industry: industryName,
            label:
              (i18n.language === Locale.EN ? description : descriptionMV) ??
              description,
            value: code,
            otherData,
          },
        ]);
      },
    );

    return maps;
  }, [data, i18n.language]);

  const genders = [
    { label: tQuotation('male'), value: Gender.MALE },
    { label: tQuotation('female'), value: Gender.FEMALE },
  ];
  const mobilePrefix = [
    { label: `+${MYS_MOBILE_PREFIX}`, value: MYS_MOBILE_PREFIX },
  ];
  const answers = [
    { label: t('yes'), value: Answer.Yes },
    { label: t('no'), value: Answer.No },
  ];

  const purposeOfTransactions =
    optionsMap.get(ReferenceKey.PurposeOfTransaction) ?? [];
  const nationalities = optionsMap.get(ReferenceKey.Nationality) ?? [];
  const religions = optionsMap.get(ReferenceKey.Religion) ?? [];
  const races = optionsMap.get(ReferenceKey.Race) ?? [];
  const countries = optionsMap.get(ReferenceKey.Country) ?? [];
  const relationships = optionsMap.get(ReferenceKey.Relationship) ?? [];
  const states = optionsMap.get(ReferenceKey.State) ?? [];
  const titles = optionsMap.get(ReferenceKey.NameTitle) ?? [];
  const maritalStatuses = optionsMap.get(ReferenceKey.MaritalStatus) ?? [];

  const occupations = optionsMap.get(ReferenceKey.OccupationalClass) ?? [];
  const occupationWithIndustry = Object.entries(
    occupations.reduce(
      (acc, { industry, ...rest }) => {
        if (industry) {
          const list = acc[industry] ?? [];
          acc[industry] = [...list, rest];
        }
        return { ...acc };
      },
      {} as {
        [industry: string]: {
          label: string;
          value: string;
        }[];
      },
    ),
  ).map(([category, options]) => ({ industry: category, options }));

  const banks = optionsMap.get(ReferenceKey.Bank) ?? [];
  const index = banks?.findIndex((b) => b.value === MAYBANK_BANK_CODE) ?? -1;
  if (index > -1) {
    const itemToMove = banks?.[index];
    banks?.splice(index, 1);
    if (itemToMove) banks?.unshift(itemToMove);
  }
  const selections = [
    Relationship.Self,
    Relationship.Spouse,
    Relationship.Son,
    Relationship.Daughter,
  ];
  const getProtections = (
    gender: Gender,
    value?: string,
  ): { logo: IconType; label: string; value: string }[] => {
    const genderMap =
      gender === Gender.FEMALE
        ? {
            [Relationship.Self]: FemaleIcon,
            [Relationship.Spouse]: MaleIcon,
          }
        : {
            [Relationship.Self]: MaleIcon,
            [Relationship.Spouse]: FemaleIcon,
          };

    const selectionsMap = {
      ...genderMap,
      [Relationship.Son]: SonIcon,
      [Relationship.Daughter]: DaughterIcon,
    };
    if (value === undefined) {
      return relationships
        .filter((relationship) =>
          selections.includes(relationship.value as Relationship),
        )
        .map((result) => ({
          logo: selectionsMap[result.value as Relationship] as IconType,
          label: result.label,
          value: result.value,
        }))
        .sort(
          (a, b) =>
            selections.indexOf(a.value as Relationship) -
            selections.indexOf(b.value as Relationship),
        );
    } else {
      const result = relationships.find(
        (relationship) => relationship.value === value,
      );
      if (!result) return []; // must return an array
      return [
        {
          logo: selectionsMap[result.value as Relationship] as IconType,
          label: result.label,
          value: result.value,
        },
      ];
    }
  };
  const relationshipMap = {
    [Relationship.Self]: MaleIcon,
    [Relationship.Spouse]: FemaleIcon,
    [Relationship.Son]: SonIcon,
    [Relationship.Daughter]: DaughterIcon,
  };
  const getInsuredIcon = (relationship: Relationship): IconType => {
    return relationshipMap[relationship];
  };

  const fundSource = optionsMap.get(ReferenceKey.SourceFund) ?? [];
  const wealthSource = optionsMap.get(ReferenceKey.SourceWealth) ?? [];

  const mapGenderToOtherData = {
    [Gender.MALE]: [OtherData.MALE],
    [Gender.FEMALE]: [OtherData.FEMALE],
  };

  return {
    purposeOfTransactions,
    genders,
    nationalities,
    mobilePrefix,
    religions,
    races,
    occupations: occupationWithIndustry,
    banks,
    countries,
    relationships,
    answers,
    states,
    titles,
    maritalStatuses,
    fundSource,
    wealthSource,
    isLoading,
    getProtections,
    getInsuredIcon,
    mapGenderToOtherData,
  };
};
